import React, { useEffect, useMemo, useRef, useState } from "react";
import db from "../../../../../offline/db";

const clampPercent = (value, fallback) => {
  const n = Number(value);
  if (Number.isFinite(n)) return Math.min(100, Math.max(0, n));
  return fallback;
};

const ColumnBackgroundMedia = ({ mediaIds, opacityPercent = 30, intervalMs = 6000 }) => {
  const ids = useMemo(() => (Array.isArray(mediaIds) ? mediaIds.map((x) => Number(x)).filter((x) => Number.isFinite(x)) : []), [mediaIds]);
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const objectUrlsRef = useRef([]);

  const opacity = clampPercent(opacityPercent, 30) / 100;

  useEffect(() => {
    let cancelled = false;

    const cleanupUrls = () => {
      const urls = objectUrlsRef.current;
      objectUrlsRef.current = [];
      urls.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {}
      });
    };

    const load = async () => {
      cleanupUrls();
      setItems([]);
      setIndex(0);

      if (ids.length === 0) return;

      try {
        const loaded = [];
        for (const id of ids) {
          const row = await db.tokenColumnBgMedia.get(id);
          if (!row || !row.blob) continue;
          const url = URL.createObjectURL(row.blob);
          objectUrlsRef.current.push(url);
          loaded.push({
            id: row.id,
            url,
            type: row.type || row.blob?.type,
            name: row.name,
          });
        }
        if (!cancelled) setItems(loaded);
      } catch (_) {
        if (!cancelled) setItems([]);
      }
    };

    load();

    return () => {
      cancelled = true;
      cleanupUrls();
    };
  }, [ids]);

  useEffect(() => {
    if (!items || items.length <= 1) return;
    const ms = Math.max(1500, Number(intervalMs) || 6000);
    const t = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, ms);
    return () => clearInterval(t);
  }, [items, intervalMs]);

  if (!items || items.length === 0) return null;

  const active = items[index] || items[0];
  const type = String(active?.type || "");
  const isVideo = type.startsWith("video/");

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        opacity,
        pointerEvents: "none",
        overflow: "hidden",
      }}
      aria-hidden="true"
    >
      {isVideo ? (
        <video
          key={active.id}
          src={active.url}
          autoPlay
          muted
          loop
          playsInline
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <img
          key={active.id}
          src={active.url}
          alt={active?.name || ""}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
    </div>
  );
};

export default ColumnBackgroundMedia;
